import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';

/**
 * Combat feedback visuals: muzzle flashes, wind-drifting powder smoke, water
 * splash plumes and hull-hit sparks/splinters.
 *
 * Everything is drawn with two fixed-pool `THREE.Points` systems:
 * - `glow`: additive particles for flashes, sparks and splash droplets.
 * - `smoke`: normally-blended soft puffs for powder smoke.
 *
 * Both pools are recycled round-robin in place and every trigger method copies
 * its inputs immediately, so there is no per-frame (or per-trigger) allocation.
 * All randomness flows through `createSeededRandom`, matching the rest of the
 * project so bot playtests and capture baselines stay reproducible.
 */

/** Seed for combat-fx jitter so visual baselines stay reproducible. */
const SEED = 0xcb47;

/** Additive glow particles (flash cores, sparks, splash droplets). */
const GLOW_POOL = 256;
/** Normally-blended smoke puffs. */
const SMOKE_POOL = 96;

/** Converts world-metre point sizes into pixels (`px = size * SCALE / depth`). */
const POINT_SCALE = 800;

// --- muzzle flash ----------------------------------------------------------

// The flash is a compact, short-lived ignition rather than a blown-out HDR orb.
// Core + halo peak just below the calm bloom threshold, so bloom only adds a
// brief, modest flare in storms (where the threshold is lower).
const FLASH_CORE_LIFE = 0.06;
const FLASH_CORE_SIZE = 0.7;
const FLASH_CORE_ALPHA = 0.75;
const FLASH_CORE_COLOR = 0xffe0b0;
/** Larger, dimmer halo that makes the flash read as a real ignition. */
const FLASH_HALO_LIFE = 0.09;
const FLASH_HALO_SIZE = 1.4;
const FLASH_HALO_ALPHA = 0.24;
const FLASH_HALO_COLOR = 0xffb45a;
/** Forward-spraying embers that give the flash its direction. */
const FLASH_SPARKS = 5;
const FLASH_SPARK_SPREAD = 0.45;
const FLASH_SPARK_SPEED = 7;
const FLASH_SPARK_LIFE = 0.12;
const FLASH_SPARK_SIZE = 0.3;

// --- powder smoke ----------------------------------------------------------

const SMOKE_PUFF_COUNT = 12;
const SMOKE_LIFE = 3.0;
const SMOKE_RISE = 1.2;
const SMOKE_JITTER = 0.7;
const SMOKE_SIZE_START = 1.6;
const SMOKE_ALPHA = 0.18;
const SMOKE_COLOR = 0x9a9aa2;

// --- water splash ----------------------------------------------------------

const SPLASH_PARTICLES = 20;
const SPLASH_LIFE = 1.0;
const SPLASH_OUT_SPEED = 3.6;
const SPLASH_UP_SPEED = 7.0;
const SPLASH_GRAVITY = 15;
const SPLASH_SIZE = 0.32;
/** White mist puffs (smoke pool) that linger over the splash column. */
const SPLASH_MIST = 7;
const SPLASH_MIST_LIFE = 1.0;
const SPLASH_MIST_RISE = 1.8;
const SPLASH_MIST_SIZE = 0.85;
const SPLASH_MIST_ALPHA = 0.24;
const SPLASH_MIST_COLOR = 0xeaf6ff;

// --- hull hit sparks -------------------------------------------------------

const HIT_FLASH_LIFE = 0.07;
const HIT_FLASH_SIZE = 0.8;
const SPARK_PARTICLES = 24;
const SPARK_LIFE = 0.75;
const SPARK_SPEED = 7.0;
const SPARK_GRAVITY = 9;
const SPARK_SIZE = 0.26;
/** Brown wood-dust puffs (smoke pool) that read as splinters. */
const HIT_DUST = 7;
const HIT_DUST_LIFE = 0.8;
const HIT_DUST_SIZE = 0.6;
const HIT_DUST_ALPHA = 0.28;
const HIT_DUST_COLOR = 0x6b5646;

interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  life: number;
  size: number;
  alpha: number;
  color: THREE.Color;
  /** Downward acceleration on airborne particles, m/s². */
  gravity: number;
}

const VERTEX_SHADER = /* glsl */ `
  attribute float aAlpha;
  attribute float aSize;
  attribute vec3 aColor;

  uniform float uScale;

  varying float vAlpha;
  varying vec3 vColor;

  void main() {
    vAlpha = aAlpha;
    vColor = aColor;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float depth = -mvPosition.z;
    gl_PointSize = (aAlpha > 0.0 && depth > 0.0) ? aSize * (uScale / depth) : 0.0;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

/** Crisp disc used for sparks, flash cores and splash droplets. */
const GLOW_FRAGMENT_SHADER = /* glsl */ `
  varying float vAlpha;
  varying vec3 vColor;

  void main() {
    if (vAlpha <= 0.002) discard;

    vec2 uv = gl_PointCoord - vec2(0.5);
    float disc = smoothstep(0.5, 0.06, length(uv));
    if (disc <= 0.004) discard;

    gl_FragColor = vec4(vColor, vAlpha * disc);
  }
`;

/** Soft, wide falloff used by powder-smoke puffs. */
const SMOKE_FRAGMENT_SHADER = /* glsl */ `
  varying float vAlpha;
  varying vec3 vColor;

  void main() {
    if (vAlpha <= 0.002) discard;

    vec2 uv = gl_PointCoord - vec2(0.5);
    float disc = 1.0 - smoothstep(0.12, 0.5, length(uv));
    if (disc <= 0.004) discard;

    gl_FragColor = vec4(vColor, vAlpha * disc);
  }
`;

export class CombatFx {
  private readonly rng = createSeededRandom(SEED);

  private readonly glow: Particle[] = [];
  private readonly smokePool: Particle[] = [];
  private glowCursor = 0;
  private smokeCursor = 0;

  private readonly glowPositions: Float32Array;
  private readonly glowAlphas: Float32Array;
  private readonly glowSizes: Float32Array;
  private readonly glowColors: Float32Array;
  private readonly smokePositions: Float32Array;
  private readonly smokeAlphas: Float32Array;
  private readonly smokeSizes: Float32Array;
  private readonly smokeColors: Float32Array;

  private readonly glowGeometry: THREE.BufferGeometry;
  private readonly glowMaterial: THREE.ShaderMaterial;
  private readonly glowPoints: THREE.Points;
  private readonly smokeGeometry: THREE.BufferGeometry;
  private readonly smokeMaterial: THREE.ShaderMaterial;
  private readonly smokePoints: THREE.Points;

  /** Reusable scratch vectors so triggers never allocate. */
  private readonly flashDir = new THREE.Vector3();
  private readonly sparkDir = new THREE.Vector3();
  private readonly windVel = new THREE.Vector3();

  constructor(private readonly scene: THREE.Scene) {
    this.glowPositions = new Float32Array(GLOW_POOL * 3);
    this.glowAlphas = new Float32Array(GLOW_POOL);
    this.glowSizes = new Float32Array(GLOW_POOL);
    this.glowColors = new Float32Array(GLOW_POOL * 3);
    this.smokePositions = new Float32Array(SMOKE_POOL * 3);
    this.smokeAlphas = new Float32Array(SMOKE_POOL);
    this.smokeSizes = new Float32Array(SMOKE_POOL);
    this.smokeColors = new Float32Array(SMOKE_POOL * 3);

    this.fillPool(this.glow, GLOW_POOL);
    this.fillPool(this.smokePool, SMOKE_POOL);

    this.glowGeometry = this.buildGeometry(
      this.glowPositions,
      this.glowAlphas,
      this.glowSizes,
      this.glowColors,
    );
    this.smokeGeometry = this.buildGeometry(
      this.smokePositions,
      this.smokeAlphas,
      this.smokeSizes,
      this.smokeColors,
    );

    this.glowMaterial = new THREE.ShaderMaterial({
      uniforms: { uScale: { value: POINT_SCALE } },
      vertexShader: VERTEX_SHADER,
      fragmentShader: GLOW_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.smokeMaterial = new THREE.ShaderMaterial({
      uniforms: { uScale: { value: POINT_SCALE } },
      vertexShader: VERTEX_SHADER,
      fragmentShader: SMOKE_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    this.glowPoints = new THREE.Points(this.glowGeometry, this.glowMaterial);
    this.glowPoints.name = 'combat-fx-glow';
    this.glowPoints.frustumCulled = false;
    this.scene.add(this.glowPoints);

    this.smokePoints = new THREE.Points(this.smokeGeometry, this.smokeMaterial);
    this.smokePoints.name = 'combat-fx-smoke';
    this.smokePoints.frustumCulled = false;
    this.scene.add(this.smokePoints);
  }

  /**
   * Emit a short, bright cannon muzzle flash oriented along `dir`.
   *
   * @param worldPos Muzzle position in world space.
   * @param dir      Aim direction of the cannon (normalised internally).
   */
  muzzleFlash(worldPos: THREE.Vector3, dir: THREE.Vector3): void {
    this.flashDir.copy(dir);
    if (this.flashDir.lengthSq() < 1e-10) {
      this.flashDir.set(0, 0, 1);
    } else {
      this.flashDir.normalize();
    }

    this.spawnGlow(
      worldPos.x,
      worldPos.y,
      worldPos.z,
      0,
      0,
      0,
      FLASH_CORE_LIFE,
      FLASH_CORE_SIZE,
      FLASH_CORE_ALPHA,
      FLASH_CORE_COLOR,
      0,
    );

    this.spawnGlow(
      worldPos.x,
      worldPos.y,
      worldPos.z,
      0,
      0,
      0,
      FLASH_HALO_LIFE,
      FLASH_HALO_SIZE,
      FLASH_HALO_ALPHA,
      FLASH_HALO_COLOR,
      0,
    );

    for (let i = 0; i < FLASH_SPARKS; i += 1) {
      this.randomUnit(this.sparkDir);
      this.sparkDir.multiplyScalar(FLASH_SPARK_SPREAD).add(this.flashDir).normalize();
      const speed = FLASH_SPARK_SPEED * (0.5 + this.rng() * 0.9);
      const life = FLASH_SPARK_LIFE * (0.5 + this.rng() * 0.6);
      const size = FLASH_SPARK_SIZE * (0.5 + this.rng());
      // Start each ember a little way out along its own direction so the whole
      // broadside does not stack into a single hot pixel at the muzzle.
      const offset = 0.2 + this.rng() * 0.4;
      this.spawnGlow(
        worldPos.x + this.sparkDir.x * offset,
        worldPos.y + this.sparkDir.y * offset,
        worldPos.z + this.sparkDir.z * offset,
        this.sparkDir.x * speed,
        this.sparkDir.y * speed,
        this.sparkDir.z * speed,
        life,
        size,
        0.45,
        this.rng() < 0.5 ? 0xffd28a : 0xfff6dc,
        0,
      );
    }
  }

  /**
   * Emit a billowing powder-smoke cloud that drifts with the wind and fades.
   *
   * @param worldPos Emit position in world space.
   * @param windDir  Wind drift velocity (world units/second, direction the wind
   *                 blows toward). Zero wind still leaves a slowly rising puff.
   */
  smoke(worldPos: THREE.Vector3, windDir: THREE.Vector3): void {
    this.windVel.copy(windDir);

    for (let i = 0; i < SMOKE_PUFF_COUNT; i += 1) {
      const drift = 0.8 + this.rng() * 0.8;
      const life = SMOKE_LIFE * (0.65 + this.rng() * 0.7);
      const size = SMOKE_SIZE_START * (0.6 + this.rng() * 0.8);
      const alpha = SMOKE_ALPHA * (0.7 + this.rng() * 0.6);
      this.spawnSmoke(
        worldPos.x + (this.rng() - 0.5) * 0.6,
        worldPos.y + 0.2 + this.rng() * 0.4,
        worldPos.z + (this.rng() - 0.5) * 0.6,
        this.windVel.x * drift + (this.rng() - 0.5) * SMOKE_JITTER,
        SMOKE_RISE * (0.6 + this.rng() * 0.8),
        this.windVel.z * drift + (this.rng() - 0.5) * SMOKE_JITTER,
        life,
        size,
        alpha,
        SMOKE_COLOR,
      );
    }
  }

  /** Emit an upward water-splash plume (used when a ball strikes the water). */
  splash(worldPos: THREE.Vector3): void {
    for (let i = 0; i < SPLASH_PARTICLES; i += 1) {
      const angle = this.rng() * Math.PI * 2;
      const outward = SPLASH_OUT_SPEED * (0.3 + this.rng() * 0.9);
      const upward = SPLASH_UP_SPEED * (0.6 + this.rng() * 0.8);
      const life = SPLASH_LIFE * (0.6 + this.rng() * 0.7);
      const size = SPLASH_SIZE * (0.5 + this.rng());
      this.spawnGlow(
        worldPos.x + (this.rng() - 0.5) * 0.3,
        worldPos.y,
        worldPos.z + (this.rng() - 0.5) * 0.3,
        Math.cos(angle) * outward,
        upward,
        Math.sin(angle) * outward,
        life,
        size,
        0.8,
        0xdff0ff,
        SPLASH_GRAVITY,
      );
    }

    for (let i = 0; i < SPLASH_MIST; i += 1) {
      this.spawnSmoke(
        worldPos.x + (this.rng() - 0.5) * 0.5,
        worldPos.y + 0.2,
        worldPos.z + (this.rng() - 0.5) * 0.5,
        (this.rng() - 0.5) * 0.8,
        SPLASH_MIST_RISE * (0.5 + this.rng() * 0.7),
        (this.rng() - 0.5) * 0.8,
        SPLASH_MIST_LIFE * (0.6 + this.rng() * 0.7),
        SPLASH_MIST_SIZE * (0.5 + this.rng()),
        SPLASH_MIST_ALPHA * (0.7 + this.rng() * 0.6),
        SPLASH_MIST_COLOR,
      );
    }
  }

  /** Emit hull-hit sparks/splinters plus a brief impact flash. */
  hitSparks(worldPos: THREE.Vector3): void {
    this.spawnGlow(
      worldPos.x,
      worldPos.y,
      worldPos.z,
      0,
      0,
      0,
      HIT_FLASH_LIFE,
      HIT_FLASH_SIZE,
      0.7,
      0xfff2c0,
      0,
    );

    for (let i = 0; i < SPARK_PARTICLES; i += 1) {
      this.randomUnit(this.sparkDir);
      if (this.sparkDir.y < 0) this.sparkDir.y = -this.sparkDir.y;
      const speed = SPARK_SPEED * (0.4 + this.rng() * 1.0);
      const life = SPARK_LIFE * (0.5 + this.rng() * 0.8);
      const size = SPARK_SIZE * (0.4 + this.rng());
      // Offset along the spark direction so the whole hit does not collapse
      // into one additive blob at the impact point.
      const offset = 0.1 + this.rng() * 0.25;
      this.spawnGlow(
        worldPos.x + this.sparkDir.x * offset,
        worldPos.y + this.sparkDir.y * offset,
        worldPos.z + this.sparkDir.z * offset,
        this.sparkDir.x * speed,
        this.sparkDir.y * speed,
        this.sparkDir.z * speed,
        life,
        size,
        0.6,
        this.rng() < 0.5 ? 0xffc966 : 0xfff2d0,
        SPARK_GRAVITY,
      );
    }

    for (let i = 0; i < HIT_DUST; i += 1) {
      this.spawnSmoke(
        worldPos.x + (this.rng() - 0.5) * 0.3,
        worldPos.y + (this.rng() - 0.5) * 0.3,
        worldPos.z + (this.rng() - 0.5) * 0.3,
        (this.rng() - 0.5) * 1.2,
        0.4 + this.rng() * 0.8,
        (this.rng() - 0.5) * 1.2,
        HIT_DUST_LIFE * (0.6 + this.rng() * 0.7),
        HIT_DUST_SIZE * (0.5 + this.rng()),
        HIT_DUST_ALPHA * (0.7 + this.rng() * 0.6),
        HIT_DUST_COLOR,
      );
    }
  }

  /** Advance both particle pools for one frame. */
  update(delta: number): void {
    const dt = Math.min(Math.max(delta, 0), 0.05);
    this.integrateGlow(dt);
    this.integrateSmoke(dt);

    this.glowGeometry.attributes.position.needsUpdate = true;
    this.glowGeometry.attributes.aAlpha.needsUpdate = true;
    this.glowGeometry.attributes.aSize.needsUpdate = true;
    this.glowGeometry.attributes.aColor.needsUpdate = true;
    this.smokeGeometry.attributes.position.needsUpdate = true;
    this.smokeGeometry.attributes.aAlpha.needsUpdate = true;
    this.smokeGeometry.attributes.aSize.needsUpdate = true;
    this.smokeGeometry.attributes.aColor.needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.glowPoints);
    this.scene.remove(this.smokePoints);
    this.glowGeometry.dispose();
    this.glowMaterial.dispose();
    this.smokeGeometry.dispose();
    this.smokeMaterial.dispose();
    this.glow.length = 0;
    this.smokePool.length = 0;
  }

  // --- setup helpers -------------------------------------------------------

  private fillPool(pool: Particle[], count: number): void {
    for (let i = 0; i < count; i += 1) {
      pool.push({
        position: new THREE.Vector3(0, -1000, 0),
        velocity: new THREE.Vector3(),
        age: 0,
        life: 0,
        size: 0,
        alpha: 0,
        color: new THREE.Color(),
        gravity: 0,
      });
    }
  }

  private buildGeometry(
    positions: Float32Array,
    alphas: Float32Array,
    sizes: Float32Array,
    colors: Float32Array,
  ): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', dynamicAttribute(new THREE.BufferAttribute(positions, 3)));
    geometry.setAttribute('aAlpha', dynamicAttribute(new THREE.BufferAttribute(alphas, 1)));
    geometry.setAttribute('aSize', dynamicAttribute(new THREE.BufferAttribute(sizes, 1)));
    geometry.setAttribute('aColor', dynamicAttribute(new THREE.BufferAttribute(colors, 3)));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);
    return geometry;
  }

  private randomUnit(out: THREE.Vector3): THREE.Vector3 {
    out.set(this.rng() * 2 - 1, this.rng() * 2 - 1, this.rng() * 2 - 1);
    if (out.lengthSq() < 1e-8) {
      out.set(0, 1, 0);
    } else {
      out.normalize();
    }
    return out;
  }

  private spawnGlow(
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    size: number,
    alpha: number,
    color: number,
    gravity: number,
  ): void {
    const particle = this.glow[this.glowCursor];
    this.glowCursor = (this.glowCursor + 1) % GLOW_POOL;
    particle.position.set(x, y, z);
    particle.velocity.set(vx, vy, vz);
    particle.age = 0;
    particle.life = life;
    particle.size = size;
    particle.alpha = alpha;
    particle.color.setHex(color);
    particle.gravity = gravity;
  }

  private spawnSmoke(
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    size: number,
    alpha: number,
    color: number,
  ): void {
    const particle = this.smokePool[this.smokeCursor];
    this.smokeCursor = (this.smokeCursor + 1) % SMOKE_POOL;
    particle.position.set(x, y, z);
    particle.velocity.set(vx, vy, vz);
    particle.age = 0;
    particle.life = life;
    particle.size = size;
    particle.alpha = alpha;
    particle.color.setHex(color);
    particle.gravity = 0;
  }

  // --- simulation ----------------------------------------------------------

  private integrateGlow(dt: number): void {
    for (let i = 0; i < GLOW_POOL; i += 1) {
      const particle = this.glow[i];
      const offset = i * 3;
      if (particle.life <= 0 || particle.age >= particle.life) {
        this.glowAlphas[i] = 0;
        this.glowSizes[i] = 0;
        continue;
      }

      particle.age += dt;
      particle.velocity.y -= particle.gravity * dt;
      particle.position.x += particle.velocity.x * dt;
      particle.position.y += particle.velocity.y * dt;
      particle.position.z += particle.velocity.z * dt;

      const t = Math.min(1, particle.age / particle.life);
      const fade = 1 - t;
      this.glowPositions[offset] = particle.position.x;
      this.glowPositions[offset + 1] = particle.position.y;
      this.glowPositions[offset + 2] = particle.position.z;
      this.glowAlphas[i] = particle.alpha * fade;
      this.glowSizes[i] = particle.size * (1 - 0.3 * t);
      this.glowColors[offset] = particle.color.r;
      this.glowColors[offset + 1] = particle.color.g;
      this.glowColors[offset + 2] = particle.color.b;
    }
  }

  private integrateSmoke(dt: number): void {
    for (let i = 0; i < SMOKE_POOL; i += 1) {
      const particle = this.smokePool[i];
      const offset = i * 3;
      if (particle.life <= 0 || particle.age >= particle.life) {
        this.smokeAlphas[i] = 0;
        this.smokeSizes[i] = 0;
        continue;
      }

      particle.age += dt;
      particle.position.x += particle.velocity.x * dt;
      particle.position.y += particle.velocity.y * dt;
      particle.position.z += particle.velocity.z * dt;

      const t = Math.min(1, particle.age / particle.life);
      const fade = (1 - t) * (1 - t);
      this.smokePositions[offset] = particle.position.x;
      this.smokePositions[offset + 1] = particle.position.y;
      this.smokePositions[offset + 2] = particle.position.z;
      this.smokeAlphas[i] = particle.alpha * fade;
      this.smokeSizes[i] = particle.size * (0.8 + 2.0 * t);
      this.smokeColors[offset] = particle.color.r;
      this.smokeColors[offset + 1] = particle.color.g;
      this.smokeColors[offset + 2] = particle.color.b;
    }
  }
}

function dynamicAttribute<T extends THREE.BufferAttribute>(attribute: T): T {
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}
